export type Tone = 'good' | 'warning' | 'poor';

// fixed: always shown, cannot be removed from the chip bar (e.g. Primary Goal).
export type ActiveFilter = { key: string; label: string; value: string; fixed?: boolean };

export type GoalRow = {
  goal: string;
  goal_key?: string;
  /* 1 when the report has decided this goal offers a Customer Type breakdown: a webhook goal
     whose webhook counts as a purchase, with conversions to split. Decided server-side so the
     rule cannot drift from the Campaign Goal Report. */
  customer_type_available?: number;
  /* The split itself, measured over the very rows that produced this row's conversions, so
     first-time + returning always equals `conversions`. It arrives with the row rather than
     being fetched separately precisely so the two cannot diverge. */
  customer_type_rows?: any[];
  spend: string;
  // Optional: null/absent means no benchmark matched; cell renders neutral.
  spendTone?: Tone | null;
  revenue: string;
  revenueTone?: Tone | null;
  conversions: number;
  conversionsTone?: Tone | null;
  cpa: string;
  cpaTone?: Tone | null;
  roas: string;
  roasTone?: Tone | null;
  visits?: number;
  visitsTone?: Tone | null;
  uniques?: number;
  uniquesTone?: Tone | null;
  goal_visits?: number;
  // Average of the scores this goal received, and how many conversions
  // carried a usable one. Undefined when the goal has no numeric value.
  avg_value?: string;
  scored?: number;
  // Word values received on this goal, biggest share first. Numbers never
  // appear here - only the labels a client actually sent as text.
  value_breakdown?: { label: string; count: number; pct: number }[];
  ucr?: string;
  ucrTone?: Tone | null;
  cr?: string;
  crTone?: Tone | null;
  aov?: string;
  aovTone?: Tone | null;
  epc?: string;
  epcTone?: Tone | null;
  acv?: string;
  acvTone?: Tone | null;
  arpu?: string;
  arpuTone?: Tone | null;
  profit?: string;
  profitTone?: Tone | null;
  roi?: string;
  roiTone?: Tone | null;
  grade?: Grade | '';
  grade_reason?: string;
  grade_rank?: number;
  variations?: GoalRowVariation[];
  device_breakdown?: GoalRow[];
  hour_breakdown?: GoalRow[];
  source_breakdown?: GoalRow[];
  /* Conversions this goal's count leaves out, and why: its exclude setting, a traffic source
     filter, several campaigns being compared, or the goal being split by test variation. Counted the same way the Undefined view of the
     Conversion Log lists them. */
  undefined_excluded?: number;
  undefined_excluded_reason?: 'goal_setting' | 'traffic_filter' | 'multi_campaign' | 'test_variation' | '';
};

export type GoalRowVariation = GoalRow & {
  variation_id: string;
  variation_name: string;
};

export type FunnelRow = {
  id: string;
  name: string;
  spend: string;
  spendTone?: Tone | null;
  revenue: string;
  revenueTone?: Tone | null;
  conversions: number;
  conversionsTone?: Tone | null;
  cpa: string;
  cpaTone?: Tone | null;
  roas: string;
  roasTone?: Tone | null;
  visits?: number;
  visitsTone?: Tone | null;
  uniques?: number;
  uniquesTone?: Tone | null;
  goal_visits?: number;
  ucr?: string;
  ucrTone?: Tone | null;
  cr?: string;
  crTone?: Tone | null;
  aov?: string;
  aovTone?: Tone | null;
  epc?: string;
  epcTone?: Tone | null;
  acv?: string;
  acvTone?: Tone | null;
  arpu?: string;
  arpuTone?: Tone | null;
  profit?: string;
  profitTone?: Tone | null;
  roi?: string;
  roiTone?: Tone | null;
  has_test_variation?: boolean;
  // Whether this campaign already carries a note; drives the Funnel row's note icon.
  has_note?: number;
};

export type Benchmark = {
  id: string;
  metric: string;
  goal_key?: string;
  goal: string;
  good: number;
  // Backend auto-grades values between Good and Poor as "warning"; legacy field.
  warning?: number;
  poor: number;
};

export type LifecycleRow = {
  entry: string;
  spend: string;
  users: string;
  book: { conv: string; convPct: string; convPctTone: Tone; cost: string; avgDays: string };
  show: { conv: string; convPct: string; convPctTone: Tone; cost: string; avgDays: string };
  purchase: { conv: string; convPct: string; convPctTone: Tone; cost: string; avgDays: string };
};

// One goal column header in the lifecycle table.
export type LifecycleGoalDef = { color: string; name: string };

// One row of cohort metrics; per-goal cells are dynamic keys like `${goalKey}_conversion`.
export type LifecycleCohortRow = {
  spend?: number | string;
  uniques?: number | string;
  revenue_per_entry?: number;
  roas?: number;
  summary_key?: string;
  [key: string]: number | string | undefined;
};

// Ad-level drilldown entry shown when a CP-Goal cell is clicked.
export type LifecycleDrilldownAd = {
  ad_id: string | number;
  ad_name: string;
  spend: number;
  entries: number;
  cp_goal: number;
};

// Top-level payload backing AggregatedLifecycleBody.
export type LifecycleData = {
  eligible: boolean;
  campaign_goals?: Record<string, LifecycleGoalDef>;
  daily_report_data?: Record<string, LifecycleCohortRow>;
  summaries?: Record<string, LifecycleCohortRow>;
  weekday_weekend_summaries?: Record<string, LifecycleCohortRow>;
  daily_cohort_drilldown?: Record<string, Record<string, LifecycleDrilldownAd[] | LifecycleDrilldownAd>>;
  cohort_drilldown?: Record<string, Record<string, LifecycleDrilldownAd[] | LifecycleDrilldownAd>>;
  selected_time?: string;
  attribution_model?: string;
};

export type AdNetworkLevel = 'campaign' | 'adset' | 'ad';

// Per-goal metric bundle returned per row by get_aggregated_ad_network_data.
// All numeric formatting (money, percent, "x") is done backend-side.
export type NetworkGoalMetric = {
  name: string;
  conversions: number;
  conversionsTone?: Tone | null;
  /* Conversions this ad was in the journey for but did not win. null - not 0 - when the
     attribution model does not name a single winning click, so the cell can say "not measured"
     rather than "assisted nothing". Breakdown sub-rows are always null: an assist belongs to
     the journey, not to the device or hour the conversion landed on. */
  assisted?: number | null;
  influenced_revenue?: string | null;
  revenue: string;
  revenueTone?: Tone | null;
  goal_visits: number;
  // Average of the scores this goal received, and how many conversions
  // carried a usable one. Undefined when the goal has no numeric value.
  avg_value?: string;
  // Maximum Allowed Value set on the webhook, shown as "4.20 / 5"; empty when none.
  score_max?: string;
  scored?: number;
  // Word values received on this goal, biggest share first. Numbers never
  // appear here - only the labels a client actually sent as text.
  value_breakdown?: { label: string; count: number; pct: number }[];
  cpa: string;
  cpaTone?: Tone | null;
  roas: string;
  roasTone?: Tone | null;
  ucr: string;
  ucrTone?: Tone | null;
  cr: string;
  crTone?: Tone | null;
  aov: string;
  aovTone?: Tone | null;
  epc: string;
  epcTone?: Tone | null;
  acv: string;
  acvTone?: Tone | null;
  arpu: string;
  arpuTone?: Tone | null;
  profit: string;
  profitTone?: Tone | null;
  roi: string;
  roiTone?: Tone | null;
  is_primary: boolean;
};

export type NetworkGoalDef = {
  key: string;
  name: string;
  type?: string;
  is_primary?: boolean;
  /* 1 when the goal carries money - a configured sales value, or revenue the report actually
     saw on it. Only such a goal may be given an Influenced Revenue column. */
  has_revenue?: number;
  // Score & Rating Display Name and Maximum Allowed Value from the webhook settings.
  score_label?: string;
  score_max?: string;
};

// Device-mode rows carry device_name/code; hour-mode rows carry hour_label/hour_of_day.
export type NetworkBreakdownSubRow = {
  device_name?: string;
  device_code?: number;
  hour_label?: string;
  hour_of_day?: number;
  spend: string;
  spendTone?: Tone | null;
  visits: number;
  visitsTone?: Tone | null;
  uniques: number;
  uniquesTone?: Tone | null;
  cpc: string;
  grade?: Grade | '';
  grade_reason?: string;
  grade_rank?: number;
  totals: { conversions: number; revenue: string; goal_visits: number };
  goals: Record<string, NetworkGoalMetric>;
};

export type NetworkRow = {
  id: string;
  source: string;
  source_label: string;
  entity_name: string;
  // Google's advertisingChannelType; blank on other networks.
  placement?: string;
  spend: string;
  spendTone?: Tone | null;
  visits: number;
  visitsTone?: Tone | null;
  uniques: number;
  uniquesTone?: Tone | null;
  cpc: string;
  cpm: string;
  ctr: string;
  // null on the Organic / Direct rows, which have no ad network to report them.
  impressions: number | null;
  network_revenue: string;
  network_conversions: number | null;
  // Event the network counts for Network Conversion; 'mixed' when the campaign's ad sets count different events.
  network_conversion_event?: string;
  grade?: Grade | '';
  grade_reason?: string;
  grade_rank?: number;
  totals: { conversions: number; revenue: string; goal_visits: number };
  goals: Record<string, NetworkGoalMetric>;
  device_breakdown?: NetworkBreakdownSubRow[];
  /* Customer Type sub-rows, and the per-goal eligibility that decides which columns can be
     split at all. Both are supplied by the report; the UI never derives them. */
  customer_type_breakdown?: NetworkBreakdownSubRow[];
  customer_type_eligible?: Record<string, number>;
  hour_breakdown?: NetworkBreakdownSubRow[];
  campaign?: string;
  adset?: string;
  ad?: string;
  parent_campaign_id?: string;
  parent_adset_id?: string;
};

export type NetworkApiPayload = {
  eligible: boolean;
  level: AdNetworkLevel;
  rows: NetworkRow[];
  hidden_rows?: NetworkRow[];
  goals: NetworkGoalDef[];
  attribution_used: string;
  is_multi_campaign: boolean;
  /* 1 when the chosen attribution model can name a single credited click, so Assisted and
     Influenced Revenue carry numbers. 0 under Same Day / Full Impact, where the tab shows
     assists_message instead. */
  assists_supported?: number;
  /* 1 when the assist figures are already in this payload. 0 means the model can produce them
     but they were not computed on load - the cell offers a View action and fetches per ad. */
  assists_loaded?: number;
  assists_message?: string;
  totals: { rows: number; spend: string };
  pagination?: {
    page: number;
    per_page: number;
    total_records: number;
    total_pages: number;
    search: string;
  };
};

// Keep in sync with backend output keys.
// Column order follows the campaign report's Select Metrics: its general columns first
// (CreateReport generalMetricsColumns), then its goal columns (goalMetricsColumns).
// Tables render in this order too, so the picker and the table always agree.
export const ALL_METRICS = [
  'Uniques',
  'Visits',
  'Spend',
  'Conversions',
  'Revenue',
  'ROAS',
  'ROI',
  'AOV',
  'ACV',
  'EPC',
  'Unique EPC',
  'Profit',
  'UCR',
  'CR',
  'CPA',
];

// Standalone metrics apply campaign-wide; others are per-goal.
export const BENCHMARK_STANDALONE_METRICS = ['Spend', 'Uniques', 'Visits'];

export const benchmarkMetricRequiresGoal = (metric: string): boolean =>
  !!metric && !BENCHMARK_STANDALONE_METRICS.includes(metric);

export const BENCHMARK_LOWER_IS_BETTER = ['Spend', 'CPA'];
export const benchmarkDirection = (metric: string): 'higher' | 'lower' =>
  BENCHMARK_LOWER_IS_BETTER.includes(metric) ? 'lower' : 'higher';

// Goal-level column order, from the campaign report's goalMetricsColumns
// (UCR, CR, CPA, EPG, ACV). Goal tables use this, not ALL_METRICS:
// the campaign report orders its goal table differently from its campaign table.
export const GOAL_METRIC_ORDER = [
  'Conversions',
  'UCR',
  'CR',
  'CPA',
  'EPC',
  'Unique EPC',
  'ACV',
  'Revenue',
  'ROAS',
  // 'Value Breakdown' is deliberately absent: it merged into 'Avg. Value',
  // which renders as Result and carries either an average or a label split.
  'Avg. Value',
];

export const DEFAULT_VISIBLE_METRICS = ['Uniques', 'Visits', 'Spend', 'Conversions', 'Revenue', 'ROAS', 'CPA'];

export const toneColor = (tone: Tone) =>
  tone === 'good' ? '#22c55e' : tone === 'warning' ? '#f59e0b' : '#ef4444';

// Row-level verdict across every benchmark at once. Kept out of ALL_METRICS so it
// stays a display column and never becomes a benchmark target itself.
export type Grade = 'good' | 'watch' | 'poor';

export const GRADE_COLUMNS = ['Grade', 'Grade Reason'];

export const GRADE_LABEL: Record<Grade, string> = {
  good: 'Good',
  watch: 'Watch',
  poor: 'Poor',
};

// Dot keeps one vivid hue; text shade flips so contrast holds on light and dark surfaces.
export const gradeColors = (grade: Grade, dark: boolean) =>
  grade === 'good'
    ? { dot: '#22c55e', text: dark ? '#4ade80' : '#15803d' }
    : grade === 'watch'
    ? { dot: '#f59e0b', text: dark ? '#fbbf24' : '#b45309' }
    : { dot: '#ef4444', text: dark ? '#f87171' : '#b91c1c' };

export const sourceChipColor = (source: string) => {
  if (source === 'Facebook') return 'blue';
  if (source === 'Google') return 'yellow';
  if (source === 'TikTok') return 'pink';
  if (source === 'ChatGPT') return 'dark';
  if (source === 'Organic') return 'teal';
  return 'gray';
};
